import React, { useState, useEffect } from 'react'
import { X, Package } from 'lucide-react'
import { calculateRemainingStock } from '../../utils/calculations'
import { formatCurrency } from '../../utils/formatters'
import './ProductSelectorModal.scss'

const ProductSelectorModal = ({
  isOpen,
  onClose,
  products,
  purchases,
  onSelectProduct,
  setSelectedProduct,
  setSalePrice,
}) => {
  const [productSearch, setProductSearch] = useState('')
  const [productSelectorImageModal, setProductSelectorImageModal] =
    useState(null)
  const productsPerPage = 5
  const [productSelectorPage, setProductSelectorPage] = useState(1)

  useEffect(() => {
    if (!isOpen) {
      setProductSearch('')
      setProductSelectorPage(1)
      setProductSelectorImageModal(null)
    }
  }, [isOpen])

  if (!isOpen) return null

  const filteredProducts = products.filter(
    (product) =>
      product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      product.code.toLowerCase().includes(productSearch.toLowerCase()),
  )

  const endIndex = productSelectorPage * productsPerPage
  const currentProducts = filteredProducts.slice(0, endIndex)
  const hasMoreProducts = endIndex < filteredProducts.length

  const loadMoreProducts = () => {
    setProductSelectorPage((prev) => prev + 1)
  }

  const resetPagination = () => {
    setProductSelectorPage(1)
  }

  const handleSelectProduct = (product) => {
    setSelectedProduct(product.id.toString())
    setSalePrice(product.basePrice)
    onSelectProduct(product)
    onClose()
    resetPagination()
  }

  return (
    <div className='modal-overlay' onClick={onClose}>
      <div
        className='modal-content lg'
        onClick={(e) => e.stopPropagation()}
        style={{ height: '80vh', position: 'relative' }}
      >
        <div
          className='modal-header product-selector-header'
          style={{
            paddingBottom: '1rem',
            maxWidth: '42rem',
            width: '92.3%',
            position: 'fixed',
            top: '10%',
            zIndex: 9,
            backgroundColor: 'white',
            borderTopLeftRadius: '0.5rem',
            borderTopRightRadius: '0.5rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}
          >
            <h3
              style={{
                fontWeight: '600',
                fontSize: '1.125rem',
                margin: 0,
              }}
            >
              Sélectionner un produit
            </h3>
            <button onClick={onClose} className='btn secondary sm'>
              <X size={16} />
            </button>
          </div>

          <div className='form-field'>
            <input
              type='text'
              value={productSearch}
              onChange={(e) => {
                setProductSearch(e.target.value)
                resetPagination()
              }}
              placeholder='Rechercher par nom ou code...'
            />
          </div>
        </div>

        <div style={{ height: '80vh' }}>
          {!currentProducts || currentProducts.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#6b7280',
                marginTop: '130px',
              }}
            >
              {productSearch
                ? 'Aucun produit trouvé'
                : 'Aucun produit enregistré'}
            </div>
          ) : (
            <>
              <div className='product-selector-grid'>
                <table className='table' style={{ marginTop: '130px' }}>
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Produit</th>
                      <th>Code</th>
                      <th>Prix</th>
                      <th>Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentProducts.map((product) => {
                      const remainingStock = calculateRemainingStock(
                        product,
                        purchases,
                      )
                      return (
                        <tr
                          style={{ cursor: 'pointer' }}
                          key={product.id}
                          onClick={() => handleSelectProduct(product)}
                        >
                          <td>
                            {product.images && product.images.length > 0 ? (
                              <div className='table-product-image'>
                                <img
                                  src={product.images[0]}
                                  alt={product.name}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setProductSelectorImageModal(product.images)
                                  }}
                                  className='clickable-image'
                                />
                                {product.images.length > 1 && (
                                  <span className='image-count-small'>
                                    +{product.images.length - 1}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className='no-image-placeholder'>
                                <Package size={20} />
                              </div>
                            )}
                          </td>
                          <td>
                            <div className='cell-content'>
                              <div className='primary-text'>
                                {product.name.length > 15
                                  ? product.name.substring(0, 15) + '...'
                                  : product.name}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className='text-sm font-medium'>
                              {product.code}
                            </span>
                          </td>
                          <td>
                            <span className='font-bold'>
                              {formatCurrency(product.basePrice)}
                            </span>
                          </td>
                          <td>
                            <div className='stock-cell'>
                              <span
                                className={`stock-value ${
                                  remainingStock < 2 && remainingStock >= 0
                                    ? 'stock-warning'
                                    : remainingStock < 0
                                      ? 'stock-critical'
                                      : ''
                                }`}
                              >
                                {remainingStock}
                              </span>
                              {remainingStock < 2 && remainingStock >= 0 && (
                                <span className='stock-indicator warning'>
                                  Faible
                                </span>
                              )}
                              {remainingStock < 0 && (
                                <span className='stock-indicator critical'>
                                  Rupture
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {hasMoreProducts && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '1rem 0',
                    borderTop: '1px solid #e5e7eb',
                    marginTop: '1rem',
                  }}
                >
                  <button
                    onClick={loadMoreProducts}
                    className='btn secondary'
                    style={{ minWidth: '150px' }}
                  >
                    Afficher plus ({filteredProducts.length - endIndex}{' '}
                    restants)
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {productSelectorImageModal && (
          <div
            className='modal-overlay'
            onClick={() => setProductSelectorImageModal(null)}
            style={{ zIndex: 60 }}
          >
            <div
              className='modal-content image-modal'
              onClick={(e) => e.stopPropagation()}
            >
              <div className='image-modal-header'>
                <button
                  onClick={() => setProductSelectorImageModal(null)}
                  className='btn secondary'
                >
                  <X size={20} />
                </button>
              </div>
              <div className='image-modal-body'>
                {productSelectorImageModal.map((data, index) => (
                  <img
                    key={index}
                    src={data}
                    alt='Image agrandie'
                    className='full-image'
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductSelectorModal
